import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { DwButtonConfig, DwComponent, DwComponentType, DwModalButtonStandardConfigs, DwModalConfig,
    DwModalHandler, DwModalResult, DwModalService, DwModalSize } from '@devwareapps/devware-cap';
import { Observable, of, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { FriendOption, ProfileFriend, ProfileFriendsService } from '../../services/profile-friends.service';
import { userProfilePath } from '../../../shared/services/profile-routing/profile-links';

@DwComponent({ key: 'fitness-profile-friends', name: 'Profile friends', componentType: DwComponentType.formSection, isGlobal: true })
@Component({ selector: 'app-profile-friends', templateUrl: './profile-friends.component.html', providers: [ProfileFriendsService],
    styleUrls: ['./profile-friends.component.scss'] })
export class ProfileFriendsComponent implements OnDestroy {
    message = ''; error = '';
    private _writes = new Subscription();
    constructor(public state: ProfileFriendsService, private dialogs: DwModalService) {}
    profileLink(friend: ProfileFriend): string { return friend.person?.ProfileSlug ? userProfilePath(friend.person.ProfileSlug) : null; }
    trackFriend(index: number, friend: ProfileFriend): number { return friend.UserChallengeFriendId; }
    open(): void {
        if (this.state.saving || this.state.uncertain) { return; }
        const config = new DwModalConfig<FriendPickerData>();
        config.title = 'Add a friend'; config.component = ProfileFriendPickerComponent;
        config.data = { state: this.state }; config.cloneData = false; config.modalSize = DwModalSize.responsive;
        config.buttons = [{ ...DwModalButtonStandardConfigs.instance.closeButton }];
        this._writes.add(this.dialogs.showModal(config).pipe(take(1)).subscribe(result => {
            if (!result.cancelled) { this.message = 'Friend added.'; this.error = ''; this.state.refresh(); }
        }));
    }
    remove(friend: ProfileFriend): void {
        if (this.state.saving || this.state.uncertain) { return; }
        this._writes.add(this.dialogs.showConfirmDialog({ title: 'Remove friend?',
            message: 'Remove ' + friend.name + ' from your friends?', useConfirmDefaults: true, showConfirmDialog: true,
        }).pipe(take(1)).subscribe(confirmed => {
            if (!confirmed) { return; }
            this.error = '';
            this._writes.add(this.state.remove(friend.UserChallengeFriendId).subscribe(() => {
                this.message = 'Friend removed.'; Promise.resolve().then(() => this.state.refresh());
            }, () => this.error = 'Unable to remove your friend. Refresh the list and try again.'));
        }));
    }
    refresh(): void { this.error = ''; this.message = ''; this.state.refresh(); }
    ngOnDestroy(): void { this._writes.unsubscribe(); }
}
interface FriendPickerData { state: ProfileFriendsService; }
@Component({ selector: 'app-profile-friend-picker', template: `
  <section *ngIf="state.state$ | async as vm">
    <label for="friend-search">Find a streaker</label><input id="friend-search" class="form-control" type="search"
      [(ngModel)]="search" placeholder="Search by name">
    <p *ngIf="state.saving" role="status"><i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Adding friend...</p>
    <dw-message *ngIf="error" [message]="{messageBody:error,severity:'error',allowClose:false}"></dw-message>
    <ng-container *ngIf="matches(vm.options) as options"><div class="friend-results">
      <div class="friend-result" *ngFor="let option of options | slice:0:50">
        <app-avatar-banner [avatarOnly]="true" [appearance]="option.person" [name]="option.name" [avatarMediaId]="option.person?.UserPictureMediaId"></app-avatar-banner>
        <span>{{ option.name }}</span><button class="btn btn-outline-primary" type="button" [attr.aria-label]="'Add ' + option.name"
          [disabled]="state.saving || state.uncertain" (click)="add(option)">Add</button>
      </div>
    </div><p *ngIf="!options.length">No streakers to add{{ search ? ' with that name' : '' }}.</p>
    <p *ngIf="options.length > 50">Showing the first 50 matches. Search by name to narrow the list.</p></ng-container>
  </section>`, styleUrls: ['./profile-friends.component.scss'] })
export class ProfileFriendPickerComponent implements OnInit, OnDestroy, DwModalHandler {
    modalConfig: DwModalConfig<FriendPickerData>;
    closeModal: EventEmitter<DwModalResult>;
    state: ProfileFriendsService;
    search = ''; error = '';
    private _subscriptions = new Subscription();
    ngOnInit(): void {
        this.state = this.modalConfig.data.state;
        this._subscriptions.add(this.state.state$.subscribe(vm => {
            if (vm.loading) { const result = new DwModalResult(); result.cancelled = true; result.closeModal = true; this.closeModal.emit(result); }
        }));
    }
    matches(options: FriendOption[]): FriendOption[] {
        const term = this.search.trim().toLocaleLowerCase();
        return options.filter(option => option.name.toLocaleLowerCase().includes(term));
    }
    add(option: FriendOption): void {
        this.error = '';
        this._subscriptions.add(this.state.add(option).subscribe(() => {
            const result = new DwModalResult(); result.closeModal = true; result.cancelled = false;
            Promise.resolve().then(() => this.closeModal.emit(result));
        }, () => this.error = 'Unable to add your friend. Close this window and refresh the list to try again.'));
    }
    buttonClick(button: DwButtonConfig): Observable<DwModalResult> {
        const result = new DwModalResult(); result.closeModal = !this.state.saving; result.cancelled = true;
        result.buttonCommand = button.command; return of(result);
    }
    ngOnDestroy(): void { this._subscriptions.unsubscribe(); }
}
